/* eslint-disable complexity */
import { MutableRefObject, useEffect, useRef, useState } from "react";

import { HeaderVariant } from "components/Layout";

import { HEADER_HEIGHT } from "../components/Header/components/HeaderWithContext";
import { animationDuration } from "../styles";
import { isSafari } from "../utils/isSafari";
import { sleep } from "../utils/sleep";

interface UseHeaderVariantOptions {
  headerVariant: HeaderVariant;
  disableElevation: boolean;
}

export const useHeaderVariant = <
  HeaderElType extends HTMLElement = HTMLDivElement,
  StickyPageHeaderElType extends HTMLElement = HTMLDivElement,
>(
  { headerVariant, disableElevation }: UseHeaderVariantOptions,
  deps: Array<unknown>
) => {
  const headerRef = useRef() as MutableRefObject<HeaderElType>;
  const stickyPageHeaderRef =
    useRef() as MutableRefObject<StickyPageHeaderElType>;
  const [mainHeaderHeight, setMainHeaderHeight] = useState(
    headerRef.current?.clientHeight ?? HEADER_HEIGHT
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (headerRef.current) {
        setMainHeaderHeight(headerRef.current.clientHeight);
      }
    });
    resizeObserver.observe(headerRef.current!);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const shadow = "5px -5px 20px 5px rgba(0, 0, 0, 0.5)";

    if (headerVariant === HeaderVariant.SEMI_STICKY) {
      // storing this here like this is an optimization
      // so that we don't need to rely on the useEffect cycle
      // to constantly un/resubscribe to the scroll event
      //

      let offset = getBoundingRectTop(headerRef.current);
      const setOffset = (newOffset: number) => {
        let actualOffset = 0;
        if (newOffset < 0) {
          actualOffset = 0;
        } else if (newOffset > mainHeaderHeight) {
          actualOffset = mainHeaderHeight;
        } else {
          actualOffset = newOffset;
        }

        if (offset !== actualOffset) {
          offset = actualOffset;
          if (headerRef.current) {
            headerRef.current.style.top = 0 - offset + "px";
          }
          if (stickyPageHeaderRef.current) {
            const stickyPageHeaderOffset = mainHeaderHeight - offset;
            stickyPageHeaderRef.current.style.top =
              stickyPageHeaderOffset + "px";
          }
        }

        const stickyPageHeaderTop = getBoundingRectTop(
          stickyPageHeaderRef.current
        );

        // special handler for Safari because when zoomed, it can
        // give pixel values that are off by up to 2 so this variance
        // makes the subheader appear 2px early on Safari, but it keeps
        // it from otherwise flickering
        const variance = isSafari ? 2 : 0;
        const headerAttachmentIsStuck =
          stickyPageHeaderRef.current && // sticky page header exists
          stickyPageHeaderTop - (mainHeaderHeight + variance - offset) <= 0; // sticky page header is stuck

        if (headerAttachmentIsStuck) {
          const container = stickyPageHeaderRef.current
            .children[0] as HTMLElement;
          container.style.visibility = "visible";
          container.style.opacity = "1";
        } else if (stickyPageHeaderRef.current) {
          const container = stickyPageHeaderRef.current
            .children[0] as HTMLElement;
          container.style.opacity = "0";
        }

        if (
          headerAttachmentIsStuck &&
          !disableElevation &&
          document.scrollingElement!.scrollTop >
            mainHeaderHeight - (mainHeaderHeight - offset)
        ) {
          if (!stickyPageHeaderRef.current!.style.boxShadow) {
            stickyPageHeaderRef.current!.style.boxShadow = shadow;
          }
        } else if (stickyPageHeaderRef.current) {
          if (stickyPageHeaderRef.current.style.boxShadow) {
            stickyPageHeaderRef.current.style.boxShadow = "";
          }
        }

        if (
          headerRef.current && // header exists
          offset !== mainHeaderHeight && // header is not hidden
          !headerAttachmentIsStuck && // there is not a secondary header
          (document.scrollingElement?.scrollTop ?? 0) - offset > 0 // header is stuck
        ) {
          if (!headerRef.current.style.boxShadow && !disableElevation) {
            headerRef.current.style.boxShadow = shadow;
          }
        } else if (headerRef.current) {
          if (headerRef.current.style.boxShadow) {
            headerRef.current.style.boxShadow = "";
          }
        }
      };

      let previousScrollTop = document.scrollingElement?.scrollTop ?? 0;
      const listener = () => {
        // This makes Safari behave like everyone else
        if ((document.scrollingElement?.scrollTop ?? 0) < 0) {
          return;
        }

        const latestScrollTop = document.scrollingElement?.scrollTop ?? 0;
        setOffset(offset + (latestScrollTop - previousScrollTop));

        previousScrollTop = latestScrollTop;
      };

      listener();
      document.addEventListener("scroll", listener);
      return () => document.removeEventListener("scroll", listener);
    } else if (headerVariant === HeaderVariant.STICKY) {
      if (headerRef.current) {
        headerRef.current.style.top = "0";
      }
      if (stickyPageHeaderRef.current) {
        stickyPageHeaderRef.current.style.top =
          (headerRef.current?.clientHeight ?? 0) + "px";
      }

      const listener = () => {
        // This makes Safari behave like everyone else
        if ((document.scrollingElement?.scrollTop ?? 0) < 0) {
          return;
        }

        const stickyPageHeaderTop = getBoundingRectTop(
          stickyPageHeaderRef.current
        );

        const headerAttachmentIsStuck =
          stickyPageHeaderRef.current && // sticky page header exists
          stickyPageHeaderTop - mainHeaderHeight === 0; // sticky page header is stuck

        if (
          headerAttachmentIsStuck &&
          document.scrollingElement?.scrollTop !== 0 &&
          !disableElevation
        ) {
          if (!stickyPageHeaderRef.current!.style.boxShadow) {
            stickyPageHeaderRef.current!.style.boxShadow = shadow;
          }
        } else if (stickyPageHeaderRef.current) {
          if (stickyPageHeaderRef.current.style.boxShadow) {
            stickyPageHeaderRef.current.style.boxShadow = "";
          }
        }
        if (headerAttachmentIsStuck) {
          const container = stickyPageHeaderRef.current
            .children[0] as HTMLElement;
          container.style.visibility = "visible";
          container.style.opacity = "1";
        } else if (stickyPageHeaderRef.current) {
          const container = stickyPageHeaderRef.current
            .children[0] as HTMLElement;
          container.style.opacity = "0";
          if (container.style.visibility !== "hidden") {
            sleep(animationDuration).then(
              () => (container.style.visibility = "hidden")
            );
          }
        }

        if (
          headerRef.current && // header exists
          !headerAttachmentIsStuck && // there is not a secondary header
          document.scrollingElement?.scrollTop !== 0 // we're not at the top of the page
        ) {
          if (!headerRef.current.style.boxShadow && !disableElevation) {
            headerRef.current.style.boxShadow = shadow;
          }
        } else if (headerRef.current && headerRef.current.style.boxShadow) {
          headerRef.current.style.boxShadow = "";
        }
      };

      listener();
      document.addEventListener("scroll", listener);
      return () => document.removeEventListener("scroll", listener);
    }
    // I wish there was a way to make this rule more dynamic
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerVariant, disableElevation, mainHeaderHeight, ...deps]);

  return {
    headerRef,
    stickyPageHeaderRef,
  };
};

const getBoundingRectTop = (element?: HTMLElement) => {
  // WARNING FOR THE FUTURE
  // All calls to getBoundingClientRect should be wrapped in
  // Math.round because at lite non-100% zoom levels in Chrome
  // like 90% & 110%, the bounding rect values get a weird float
  // issue that makes them just barely non-exact, which becomes
  // a major issue in cases here where we're expecting them to
  // have strict equivalence with other values
  return Math.round(element?.getBoundingClientRect().top ?? 0);
};
